import { Box, Paper, Typography } from '@mui/material';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { getClientInfo } from '@selectors/client';
import { getTheme } from '@commons/utils/theme';
import { showErrorMessage } from '@actions/messageconfirmation';
import ComponentLoader from '@commons/DeepsightComponents/ComponentLoader';

import { fetchTimeSeriesData } from './utils/fetch';
import { getMetricOptions } from './utils/config';
import ChartTooltip from '../common/ChartTooltip';

const GlobalStoreTimeSeriesChart = ({
  selectedStores,
  currentPeriod,
  previousPeriod,
  showErrorMessage,
  initialSelectedStore,
  initialSelectedMetric,
  client: { storeName },
  enableTicketsNumber,
}) => {
  const metricOptions = getMetricOptions(enableTicketsNumber);
  const [selectedStoreForChart, setSelectedStoreForChart] = useState(null);
  const [selectedMetricForChart, setSelectedMetricForChart] = useState(metricOptions[0]);
  const [chartData, setChartData] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);

  const theme = getTheme();

  const loadData = async () => {
    setIsLoadingChart(true);
    try {
      const newChartData = await fetchTimeSeriesData(
        selectedStoreForChart.id,
        currentPeriod,
        previousPeriod,
      );
      setChartData(newChartData);
    } catch (error) {
      setChartData([]);
      showErrorMessage(i18next.t('GENERAL.GLOBAL_FETCH_ERROR'));
    } finally {
      setIsLoadingChart(false);
    }
  };

  // Initialize selected store when stores are available or when initial values change
  useEffect(() => {
    if (initialSelectedStore) {
      setSelectedStoreForChart(initialSelectedStore);
    } else if (selectedStores && selectedStores.length > 0 && !selectedStoreForChart) {
      setSelectedStoreForChart(selectedStores[0]);
    }
  }, [selectedStores, initialSelectedStore]);

  // Initialize selected metric when initial value changes
  useEffect(() => {
    if (initialSelectedMetric) {
      setSelectedMetricForChart(initialSelectedMetric);
    }
  }, [initialSelectedMetric]);

  // Generate chart data only when filters change
  useEffect(() => {
    if (
      currentPeriod.startDate &&
      currentPeriod.endDate &&
      previousPeriod.startDate &&
      previousPeriod.endDate &&
      selectedStoreForChart &&
      !isLoadingChart
    )
      loadData();
  }, [
    selectedStoreForChart,
    currentPeriod.startDate,
    currentPeriod.endDate,
    previousPeriod.startDate,
    previousPeriod.endDate,
  ]);

  return (
    <Paper sx={{ p: 3, height: 500, border: '1px solid #E0E0E0', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('ANALYSIS.PERFORMANCE.GLOBAL_TIME_SERIES_CHART_TITLE', { storeName })}
        </Typography>

        {/* Filtres */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Dropdown Restaurants */}
          <Dropdown
            customStyle={{
              width: '300px',
            }}
            isRequired={true}
            isUniqueSelection={true}
            items={selectedStores || []}
            label={i18next.t('GENERAL.STORE')}
            searchPlaceholder={i18next.t('GENERAL.SEARCH')}
            selectedItem={selectedStoreForChart}
            onSelectionChange={setSelectedStoreForChart}
          />

          {/* Dropdown Métrique */}
          <Dropdown
            customStyle={{
              width: '200px',
            }}
            isRequired={true}
            isSearchDisabled={true}
            isUniqueSelection={true}
            items={metricOptions || []}
            label={i18next.t('ANALYSIS.PERFORMANCE.METRIC_DROPDOWN_LABEL')}
            selectedItem={selectedMetricForChart}
            onSelectionChange={setSelectedMetricForChart}
          />
        </Box>
      </Box>

      {/* Line Chart */}
      {selectedStoreForChart && selectedMetricForChart ? (
        isLoadingChart ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              height: 350,
              justifyContent: 'center',
            }}
          >
            <ComponentLoader height={50} width={50} />
          </Box>
        ) : (
          <ResponsiveContainer height={350} width="100%">
            <LineChart data={chartData} margin={{ bottom: 5, left: 20, right: 20, top: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="formattedDates.current" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                content={
                  <ChartTooltip
                    options={metricOptions}
                    payloadLabel="formattedDates"
                    selectedMetric={selectedMetricForChart}
                  />
                }
              />
              <Legend />

              <Line
                activeDot={{ r: 6, stroke: theme.colors.greys.medium, strokeWidth: 2 }}
                dataKey={`previous.${selectedMetricForChart.id}`}
                dot={{ fill: theme.colors.greys.medium, strokeWidth: 2, r: 4 }}
                name={i18next.t('ANALYSIS.PERFORMANCE.PREVIOUS_PERIOD')}
                stroke={theme.colors.greys.medium}
                strokeWidth={3}
                type="monotone"
              />
              <Line
                activeDot={{ r: 6, stroke: theme.colors.blue, strokeWidth: 2 }}
                dataKey={`current.${selectedMetricForChart.id}`}
                dot={{ fill: theme.colors.blue, strokeWidth: 2, r: 4 }}
                name={i18next.t('ANALYSIS.PERFORMANCE.CURRENT_PERIOD')}
                stroke={theme.colors.blue}
                strokeWidth={3}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        )
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 350,
          }}
        >
          <Typography color="text.secondary" variant="body1">
            {i18next.t('GENERAL.NO_DATA_TO_DISPLAY')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps, mapDispatchToProps)(GlobalStoreTimeSeriesChart);
